import { Link } from 'react-router-dom';
import { Check, ArrowRight, Shield, Zap, Heart, HelpCircle, X } from 'lucide-react';
import { useState, useEffect } from 'react';
import { tracking } from '../utils/tracking';

export function PricingPage() {
  const [openFaq, setOpenFaq] = useState<number | null>(null);

  useEffect(() => {
    document.title = 'Pricing — VZMO AI Life Coach | Start Free';
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute(
        'content',
        'VZMO pricing: free plan with 3 coaching sessions/week, or unlimited access for $9/month. No credit card required. Cancel anytime. 94% less than a human coach.'
      );
    }
    tracking.pricingPageViewed();
  }, []);

  const freeFeatures = [
    '3 coaching sessions per week',
    '10 journal entries per month',
    'Daily progress tracking',
    'Personalized prompts',
    'Private and anonymous',
    'Export your data anytime'
  ];

  const premiumFeatures = [
    'Unlimited AI coaching sessions',
    'Daily journal with smart prompts',
    'Weekly progress tracking',
    'Personalized action plans',
    'Private and anonymous',
    'Export your data anytime',
    'Cancel anytime, no hassle'
  ];

  const faqs = [
    {
      q: 'Is the free plan really free?',
      a: 'Yes. The free plan is free forever — no credit card, no trial that expires. You get 3 coaching sessions per week and 10 journal entries per month. If you want unlimited access, premium is $9/month.'
    },
    {
      q: 'How does this compare to hiring a human coach?',
      a: 'Human coaches on platforms like Strawberry.me typically charge $80–$250 per session — about $160/month for 2 sessions. VZMO is $9/month for unlimited access. The tradeoff: human coaches bring lived experience and a real relationship. VZMO brings instant availability, daily accountability, and a cost that means you can actually use it every day. Many people use both.'
    },
    {
      q: 'Can I cancel?',
      a: 'Yes, anytime. It takes about 10 seconds. Your data stays until you choose to delete it.'
    },
    {
      q: 'Is this therapy?',
      a: "No, it's coaching. Think of it like having a thoughtful friend who helps you think through things. If you're dealing with something serious, please talk to a licensed therapist."
    },
    {
      q: 'Is my data private?',
      a: 'Yes. No public profiles, no social features. Your entries are encrypted and we never sell your data or use it to train AI models.'
    },
    {
      q: 'What if I have questions?',
      a: 'Email us at team@vzmo.ai. Real humans reply, usually within a day.'
    }
  ];

  return (
    <div className="bg-white pt-20">
      <section className="py-20 bg-gradient-to-b from-[#FDF8F4] to-white">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <p className="inline-block text-coral-600 font-medium text-sm tracking-wide uppercase mb-4">
            Simple pricing
          </p>
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-heading font-bold text-slate-900 mb-6 leading-tight">
            Start free.
            <span className="block text-coral-500">Upgrade when ready.</span>
          </h1>
          <p className="text-xl text-slate-600 max-w-2xl mx-auto leading-relaxed mb-6">
            Most users upgrade after their first week. When you hit the limit, it's $9/month — unlimited.
          </p>
          <div className="inline-flex items-center gap-2 bg-amber-50 border border-amber-200 rounded-full px-4 py-2 text-sm text-amber-800">
            <span className="font-semibold">94% less</span>
            <span>than a human coaching platform</span>
            <Link to="/compare/strawberry-me-alternative" className="text-amber-700 underline underline-offset-2 hover:text-amber-900 font-medium">
              See comparison
            </Link>
          </div>
        </div>
      </section>

      <section className="pb-16 bg-white">
        <div className="max-w-5xl mx-auto px-4">
          <div className="grid md:grid-cols-2 gap-6">
            {/* Free plan */}
            <div className="bg-white rounded-3xl shadow-xl overflow-hidden border-2 border-slate-200 flex flex-col">
              <div className="p-8 text-center bg-gradient-to-b from-slate-50 to-white">
                <div className="inline-flex items-center gap-2 px-4 py-2 bg-blue-100 rounded-full text-blue-700 text-sm font-medium mb-6">
                  <Zap className="h-4 w-4" />
                  <span>Free Forever</span>
                </div>
                <div className="flex items-baseline justify-center gap-1 mb-2">
                  <span className="text-7xl font-heading font-bold text-slate-900">$0</span>
                  <span className="text-xl text-slate-500">/month</span>
                </div>
                <p className="text-slate-500">Perfect for getting started</p>
              </div>

              <div className="p-8 flex-1 flex flex-col">
                <ul className="space-y-4 mb-8 flex-1">
                  {freeFeatures.map((feature, index) => (
                    <li key={index} className="flex items-center gap-4">
                      <div className="w-6 h-6 rounded-full bg-slate-100 flex items-center justify-center flex-shrink-0">
                        <Check className="h-3.5 w-3.5 text-slate-600" />
                      </div>
                      <span className="text-slate-700">{feature}</span>
                    </li>
                  ))}
                </ul>

                <Link
                  to="/signup"
                  className="group w-full inline-flex items-center justify-center px-8 py-4 bg-slate-900 text-white font-semibold text-lg rounded-full hover:bg-slate-800 transition-all duration-200 shadow-lg hover:shadow-xl"
                >
                  Get started free
                  <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform duration-200" />
                </Link>

                <div className="flex items-center justify-center gap-6 mt-4 text-sm text-slate-500">
                  <div className="flex items-center gap-1.5">
                    <Shield className="h-4 w-4" />
                    <span>No card required</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Premium plan */}
            <div className="relative flex flex-col">
              <div className="absolute -inset-1 bg-gradient-to-r from-coral-200 to-coral-100 rounded-[2rem] blur-2xl opacity-60" />

              <div className="relative bg-white rounded-3xl shadow-2xl overflow-hidden border-2 border-coral-400 flex flex-col flex-1">
                <div className="absolute top-0 right-0 bg-coral-500 text-white px-4 py-1 text-xs font-bold rounded-bl-lg">
                  POPULAR
                </div>

                <div className="p-8 text-center bg-gradient-to-b from-coral-50 to-white">
                  <div className="inline-flex items-center gap-2 px-4 py-2 bg-coral-100 rounded-full text-coral-700 text-sm font-medium mb-6">
                    <Zap className="h-4 w-4" />
                    <span>Premium</span>
                  </div>

                  <div className="flex items-baseline justify-center gap-1 mb-2">
                    <span className="text-7xl font-heading font-bold text-slate-900">$9</span>
                    <span className="text-xl text-slate-500">/month</span>
                  </div>
                  <p className="text-slate-500">Unlimited everything</p>
                </div>

                <div className="p-8 flex-1 flex flex-col">
                  <ul className="space-y-4 mb-6 flex-1">
                    {premiumFeatures.map((feature, index) => (
                      <li key={index} className="flex items-center gap-4">
                        <div className="w-6 h-6 rounded-full bg-coral-100 flex items-center justify-center flex-shrink-0">
                          <Check className="h-3.5 w-3.5 text-coral-600" />
                        </div>
                        <span className="text-slate-700 font-medium">{feature}</span>
                      </li>
                    ))}
                  </ul>

                  {/* Human coaching cost callout */}
                  <div className="bg-slate-50 rounded-2xl p-4 mb-6 border border-slate-100">
                    <p className="text-xs text-slate-500 mb-2">vs. human coaching platforms</p>
                    <div className="flex items-center justify-between gap-2">
                      <div className="flex items-center gap-3">
                        <div className="text-center">
                          <p className="text-xl font-bold text-slate-400 line-through">$160</p>
                          <p className="text-xs text-slate-400">avg/mo</p>
                        </div>
                        <X className="h-4 w-4 text-slate-300" />
                        <div className="text-center">
                          <p className="text-xl font-bold text-coral-600">$9</p>
                          <p className="text-xs text-coral-500">unlimited</p>
                        </div>
                      </div>
                      <Link
                        to="/compare/strawberry-me-alternative"
                        className="text-xs text-slate-500 hover:text-coral-600 underline underline-offset-2 transition-colors"
                      >
                        Full comparison →
                      </Link>
                    </div>
                  </div>

                  <Link
                    to="/signup"
                    className="group w-full inline-flex items-center justify-center px-8 py-4 bg-coral-500 text-white font-semibold text-lg rounded-full hover:bg-coral-600 transition-all duration-200 shadow-lg hover:shadow-xl shadow-coral-500/25"
                  >
                    Start free, upgrade later
                    <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform duration-200" />
                  </Link>

                  <div className="flex items-center justify-center gap-6 mt-4 text-sm text-slate-500">
                    <div className="flex items-center gap-1.5">
                      <Heart className="h-4 w-4" />
                      <span>Cancel anytime</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Cost anchor strip */}
          <div className="mt-10 bg-slate-900 rounded-2xl p-6 sm:p-8">
            <p className="text-center text-slate-400 text-sm font-medium uppercase tracking-widest mb-6">
              Put $9/month in context
            </p>
            <div className="grid grid-cols-3 gap-4 text-center">
              <div>
                <p className="text-slate-400 text-sm mb-1 leading-snug">Human coach</p>
                <p className="text-2xl sm:text-3xl font-heading font-bold text-slate-400">$160</p>
                <p className="text-slate-500 text-xs mt-1">per month avg</p>
              </div>
              <div>
                <p className="text-slate-400 text-sm mb-1 leading-snug">Therapy</p>
                <p className="text-2xl sm:text-3xl font-heading font-bold text-slate-400">$150</p>
                <p className="text-slate-500 text-xs mt-1">per session</p>
              </div>
              <div className="relative">
                <div className="absolute inset-0 bg-coral-500/10 rounded-xl border border-coral-500/30" />
                <div className="relative py-1">
                  <p className="text-white text-sm mb-1 font-medium leading-snug">VZMO</p>
                  <p className="text-2xl sm:text-3xl font-heading font-bold text-white">$9</p>
                  <p className="text-coral-400 text-xs mt-1">unlimited/mo</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-24 bg-[#FDFBF7]">
        <div className="max-w-3xl mx-auto px-4">
          <div className="text-center mb-16">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-white rounded-full text-slate-700 text-sm font-medium mb-6 shadow-sm">
              <HelpCircle className="h-4 w-4" />
              <span>Got questions?</span>
            </div>
            <h2 className="text-3xl sm:text-4xl font-heading font-bold text-slate-900">
              Frequently asked
            </h2>
          </div>

          <div className="bg-white rounded-3xl p-6 sm:p-8 shadow-sm">
            {faqs.map((faq, index) => (
              <div key={index} className="border-b border-slate-100 last:border-b-0">
                <button
                  onClick={() => setOpenFaq(openFaq === index ? null : index)}
                  className="w-full flex items-center justify-between py-6 text-left group"
                >
                  <span className="font-medium text-slate-900 pr-8 group-hover:text-coral-600 transition-colors text-lg">
                    {faq.q}
                  </span>
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 transition-all duration-200 ${
                    openFaq === index ? 'bg-coral-100 text-coral-600 rotate-180' : 'bg-slate-100 text-slate-400 group-hover:bg-slate-200'
                  }`}>
                    <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </div>
                </button>
                <div
                  className={`overflow-hidden transition-all duration-300 ${
                    openFaq === index ? 'max-h-96 pb-6' : 'max-h-0'
                  }`}
                >
                  <p className="text-slate-600 leading-relaxed">{faq.a}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24 bg-slate-900">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-3xl sm:text-4xl font-heading font-bold text-white mb-6">
            Coaching every day, not once a week
          </h2>
          <p className="text-xl text-slate-300 mb-10">
            Start free. No credit card required. Upgrade only when you're ready.
          </p>

          <Link
            to="/signup"
            className="inline-flex items-center px-10 py-4 bg-coral-500 text-white rounded-full font-semibold text-lg hover:bg-coral-600 transition-all shadow-lg shadow-coral-500/25"
          >
            Get started free
            <ArrowRight className="ml-2 h-5 w-5" />
          </Link>

          <p className="text-slate-500 text-sm mt-6">
            Not a replacement for human coaching when you need that depth.{' '}
            <Link to="/compare/strawberry-me-alternative" className="text-slate-400 hover:text-slate-300 underline underline-offset-2 transition-colors">
              See how it compares.
            </Link>
          </p>
        </div>
      </section>
    </div>
  );
}
